import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { boFetch } from "@/lib/backoffice.server";

export type CookieCategory = {
  key: string;
  required: boolean;
  default_enabled: boolean;
  label_i18n: Record<string, string>;
  description_i18n: Record<string, string>;
};

export type CookieTranslation = {
  title: string;
  description: string;
  accept_all: string;
  reject_all: string;
  customize: string;
  save_preferences: string;
  policy_link_label: string;
  policy_link_url: string;
  dns_label: string;
  limit_sensitive_label: string;
};

export type CookieBannerConfig =
  | { enabled: false }
  | {
      enabled: true;
      policy_version: string;
      regions: ("eu" | "uk_ch" | "us_ca" | "br")[];
      locales: string[];
      default_locale: string;
      appearance: {
        position: "bottom" | "top" | "modal";
        theme: "light" | "dark" | "auto";
        accent_color: string;
        show_logo: boolean;
      };
      behavior: {
        block_until_consent: boolean;
        reshow_after_days: number;
        granular_per_purpose: boolean;
        show_reject_all: boolean;
        show_customize: boolean;
        geo_targeted: boolean;
      };
      categories: CookieCategory[];
      controller: {
        company_name: string;
        legal_address: string;
        contact_email: string;
        dpo_email: string;
      };
      translations: Record<string, CookieTranslation>;
    };

export const getCookieBanner = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z.object({ locale: z.string().min(2).max(8).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data }): Promise<CookieBannerConfig> => {
    try {
      const res = await boFetch<CookieBannerConfig>("/api/public/v1/cookie-banner", {
        query: { locale: data.locale },
        cache: { freshSec: 60, staleSec: 7 * 24 * 60 * 60 },
      });
      return res ?? { enabled: false };
    } catch (e) {
      console.warn("cookie-banner fetch failed", e);
      return { enabled: false };
    }
  });
